import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Container,
  Grid,
  Stack,
  TextField,
  Typography,
  Chip,
} from "@mui/material";
import { call } from "../../../firebase";
import { createDeck, deleteDeckCascade, getSrsStreak, importCards, listDecks } from "../firestore";

export default function DeckListPage() {
  const [decks, setDecks] = useState([]);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [deletingId, setDeletingId] = useState("");
  const [error, setError] = useState("");
  const [generateError, setGenerateError] = useState("");
  const [promptText, setPromptText] = useState("");
  const [targetCount, setTargetCount] = useState(20);
  const [streak, setStreak] = useState({ streakCount: 0, lastStudyDate: null });

  const loadDecks = async () => {
    setLoading(true);
    setError("");
    try {
      const [data, streakData] = await Promise.all([listDecks(), getSrsStreak()]);
      setDecks(data);
      setStreak(streakData);
    } catch (err) {
      setError(err.message || "Failed to load decks.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDecks();
  }, []);

  const handleCreate = async (event) => {
    event.preventDefault();
    if (!name.trim()) {
      setError("Deck name is required.");
      return;
    }

    setSaving(true);
    setError("");
    try {
      await createDeck({ name, description });
      setName("");
      setDescription("");
      await loadDecks();
    } catch (err) {
      setError(err.message || "Failed to create deck.");
    } finally {
      setSaving(false);
    }
  };

  const handleGenerateDeck = async () => {
    setGenerateError("");
    if (!promptText.trim()) {
      setGenerateError("Please enter a topic or prompt.");
      return;
    }
    setGenerating(true);
    try {
      const res = await call("generateSrsDeck", {
        prompt: promptText.trim(),
        targetCount: Number(targetCount) || 20,
      });
      const generated = res?.data?.deck || res?.data;
      if (!generated?.cards?.length) throw new Error("No cards returned from GPT.");

      const deckId = await createDeck({
        name: generated.name || "Generated Deck",
        description: generated.description || "Generated by GPT",
      });
      await importCards(deckId, generated.cards);
      setPromptText("");
      await loadDecks();
    } catch (err) {
      const msg = err?.code === "functions/not-found"
        ? "generateSrsDeck is not deployed. Deploy Firebase functions first."
        : err.message || "Failed to generate deck.";
      setGenerateError(msg);
    } finally {
      setGenerating(false);
    }
  };

  const handleDeleteDeck = async (deck) => {
    if (!window.confirm(`Delete deck "${deck.name}" and all its cards, sessions, and reviews?`)) return;
    setDeletingId(deck.id);
    setError("");
    try {
      await deleteDeckCascade(deck.id);
      await loadDecks();
    } catch (err) {
      setError(err.message || "Failed to delete deck.");
    } finally {
      setDeletingId("");
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Stack spacing={3}>
        <Box>
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={1.5}
            alignItems={{ md: "center" }}
            justifyContent="space-between"
          >
            <Typography variant="h4" fontWeight={800}>Janki Decks</Typography>
            <Card variant="outlined" sx={{ px: 1.5, py: 1, minWidth: { md: 280 } }}>
              <Stack direction="row" spacing={1.25} alignItems="center" flexWrap="wrap">
                <Chip size="small" label="Streak" color="primary" />
                <Typography variant="body2">Current: <strong>{streak.streakCount}</strong> day{streak.streakCount === 1 ? "" : "s"}</Typography>
                <Typography variant="caption" color="text.secondary">Last active: {streak.lastStudyDate || "never"}</Typography>
              </Stack>
            </Card>
          </Stack>
          <Typography color="text.secondary">Create a deck, import cards, then study daily due cards.</Typography>
          <Button component={Link} to="/srs/master-study" variant="contained" sx={{ mt: 1.5 }}>
            Master Study (All Due Cards)
          </Button>
        </Box>

        {error ? <Alert severity="error">{error}</Alert> : null}

        <Box component="form" onSubmit={handleCreate}>
          <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
            <TextField
              label="Deck name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              fullWidth
              required
            />
            <TextField
              label="Description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              fullWidth
            />
            <Button type="submit" variant="contained" disabled={saving} sx={{ minWidth: 140 }}>
              {saving ? "Creating..." : "Create Deck"}
            </Button>
          </Stack>
        </Box>

        <Box>
          <Stack spacing={1}>
            <Typography variant="h6" fontWeight={700}>Generate with GPT</Typography>
            <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
              <TextField
                label="Topic or prompt"
                value={promptText}
                onChange={(e) => setPromptText(e.target.value)}
                fullWidth
                placeholder="e.g., Grade 10 biology cell structures and functions"
              />
              <TextField
                label="Cards"
                type="number"
                value={targetCount}
                onChange={(e) => setTargetCount(e.target.value)}
                inputProps={{ min: 5, max: 100 }}
                sx={{ width: { xs: "100%", md: 120 } }}
              />
              <Button variant="outlined" onClick={handleGenerateDeck} disabled={generating}>
                {generating ? "Generating..." : "Generate Deck"}
              </Button>
            </Stack>
            {generateError ? <Alert severity="error">{generateError}</Alert> : null}
          </Stack>
        </Box>

        {loading ? (
          <Typography>Loading decks...</Typography>
        ) : decks.length === 0 ? (
          <Typography color="text.secondary">No decks yet. Create your first deck above.</Typography>
        ) : (
          <Grid container spacing={2}>
            {decks.map((deck) => (
              <Grid item xs={12} md={6} lg={4} key={deck.id}>
                <Card
                  sx={{
                    height: "100%",
                    borderRadius: 2.5,
                    border: "1px solid rgba(0,0,0,0.08)",
                    boxShadow: "0 8px 24px rgba(2, 6, 23, 0.06)",
                    background: "linear-gradient(180deg, #ffffff 0%, #f8fbff 100%)",
                  }}
                >
                  <CardContent sx={{ pb: 0.5 }}>
                    <Typography variant="h6" fontWeight={800} sx={{ lineHeight: 1.2 }}>{deck.name}</Typography>
                    <Typography color="text.secondary" sx={{ minHeight: 48, mt: 0.75 }}>
                      {deck.description || "No description"}
                    </Typography>
                  </CardContent>
                  <CardActions sx={{ p: 2, pt: 1.25, gap: 1, flexWrap: "wrap" }}>
                    <Button component={Link} to={`/srs/decks/${deck.id}`} size="small" variant="contained" sx={{ fontWeight: 700 }}>
                      Open Deck
                    </Button>
                    <Button component={Link} to={`/srs/decks/${deck.id}/study`} size="small" variant="outlined" sx={{ fontWeight: 700 }}>
                      Study
                    </Button>
                    <Button
                      size="small"
                      color="error"
                      variant="outlined"
                      onClick={() => handleDeleteDeck(deck)}
                      disabled={deletingId === deck.id}
                      sx={{ fontWeight: 700 }}
                    >
                      {deletingId === deck.id ? "Deleting..." : "Delete"}
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Stack>
    </Container>
  );
}
